import { MixerEngine } from '../audio/MixerEngine';
import type { GraphPort, NodePort } from '../audio/types';
import { StretchTransport } from './StretchTransport';
export class WebMixerEngine extends MixerEngine {
  readonly audioContext: AudioContext;
  private keepPlayingWhenHidden = true;
  private cleanup: (() => void)[] = [];
  private constructor(context: AudioContext, limiter: AudioWorkletNode, transport: StretchTransport) {
    super(context as unknown as GraphPort, limiter as unknown as NodePort, transport);
    transport.connectChannels(this.faders as unknown as Record<import('../audio/types').StemId, GainNode>);
    transport.onFailure = error => this.reportError(error);
    this.audioContext = context;
    const visibility = () => { if (document.hidden && !this.keepPlayingWhenHidden) this.pause('Browser hidden / screen locked (MVP suspension policy)'); };
    document.addEventListener('visibilitychange', visibility);
    this.cleanup.push(() => document.removeEventListener('visibilitychange', visibility));
    const interrupted = () => { if (context.state !== 'running') this.pause('Audio context suspended or interrupted'); };
    context.addEventListener('statechange', interrupted);
    this.cleanup.push(() => context.removeEventListener('statechange', interrupted));
    if (navigator.mediaDevices?.addEventListener) {
      const route = () => this.pause('Audio device changed');
      navigator.mediaDevices.addEventListener('devicechange', route);
      this.cleanup.push(() => navigator.mediaDevices.removeEventListener('devicechange', route));
    }
    this.installMediaSession();
  }
  setKeepPlayingWhenScreenLocks(_enabled: boolean): void { this.keepPlayingWhenHidden = true; }
  static async create(): Promise<WebMixerEngine> {
    // iOS Safari mutes Web Audio when the ringer switch is off unless the page declares a playback session.
    try { const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession; if (session) session.type = 'playback'; } catch { /* unsupported */ }
    // Pre-recorded stems never need minimum latency; larger output buffers mean fewer dropouts on phones.
    const context = new AudioContext({ latencyHint: 'playback' });
    try {
      await context.resume();
      await context.audioWorklet.addModule('./limiter.worklet.js');
      const limiter = new AudioWorkletNode(context, 'wm-master-limiter', { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2], channelCount: 2, channelCountMode: 'explicit' });
      const transport = await StretchTransport.create(context);
      return new WebMixerEngine(context, limiter, transport);
    } catch (e) { await context.close(); throw e; }
  }
  async dispose(): Promise<void> { this.cleanup.forEach(fn => fn()); await super.dispose(); }
  async setLoop(section: import('../audio/types').Section | null): Promise<void> {
    if(section)throw new Error('Section looping is unavailable in this timeline-only web mixer.');
    await super.setLoop(null);
  }
  private installMediaSession(): void {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({ title: 'Hope Mixer' });
    const actions: Partial<Record<MediaSessionAction, MediaSessionActionHandler>> = {
      play: () => { void this.play().catch(e => this.pause(String(e))); }, pause: () => this.pause(),
      stop: () => { this.pause(); void this.seek(0); },
      seekbackward: event => { void this.skip(-(event.seekOffset ?? 10)); },
      seekforward: event => { void this.skip(event.seekOffset ?? 10); },
      seekto: event => { if (event.seekTime !== undefined) void this.seek(event.seekTime * this.snapshot().playback.rate); }
    };
    for (const [action, handler] of Object.entries(actions)) {
      try { navigator.mediaSession.setActionHandler(action as MediaSessionAction, handler!); this.cleanup.push(() => navigator.mediaSession.setActionHandler(action as MediaSessionAction, null)); } catch { /* browser does not expose this action */ }
    }
    this.cleanup.push(this.subscribe(state => {
      navigator.mediaSession.playbackState = state.status === 'playing' ? 'playing' : 'paused';
      if (state.duration > 0) try { navigator.mediaSession.setPositionState({ duration: state.duration / state.playback.rate, playbackRate: 1, position: Math.max(0, Math.min(state.duration, state.position)) / state.playback.rate }); } catch { /* unsupported position state */ }
    }));
  }
}
